import React, { useEffect, useMemo, useState } from 'react';

export default function SupportTicketsOverlay({ onClose }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Responsive breakpoint (treat <= 720px as mobile)
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 720px)').matches : false
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 720px)');
    const onChange = (e) => setIsMobile(!!e.matches);

    // Safari support
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);

    setIsMobile(mq.matches);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        setErr(null);
        // Request only open tickets from backend
        const res = await fetch('/support-tickets/?limit=200&status=open');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (mounted && Array.isArray(data.tickets)) {
          // Fallback filter (ensure only open) + normalize filename/title
          const openOnly = data.tickets
            .filter((t) => String(t.status || '').toLowerCase() === 'open')
            .map((t) => ({
              ...t,
              title: String(t.title ?? '').trim(),
              contents: String(t.contents ?? '').trim(),
              // Ensure we always have a raw filename field for updates
              filename: String((t.filename ?? t.project ?? t.folder ?? '')).trim(),
            }));
          setTickets(openOnly);
        }
      } catch (e) {
        if (mounted) setErr(e.message || 'Failed to load tickets');
        console.error('Support tickets fetch failed:', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const baseName = (p) => {
    if (!p) return '';
    const parts = String(p).split(/[\\/]/);
    return parts[parts.length - 1];
  };

  // Title: explicit t.title; else first line of contents; else type; else filename; else placeholder
  const buildTitle = (t) => {
    const explicit = (t.title || '').trim();
    if (explicit) return explicit.slice(0, 120);

    const c = (t.contents || '').trim();
    if (c) return c.split('\n')[0].slice(0, 120);

    if (t.type) return String(t.type);
    if (t.filename) return baseName(t.filename);
    return '(No title)';
  };

  const onRowActivate = (t) => {
    setSelected(t);
    setReplyDraft(t.reply || '');
  };

  const handleReplyAndClose = async () => {
    if (!selected) return;
    try {
      setSubmitting(true);
      setErr(null);

      const hasFilename = !!(selected?.filename && String(selected.filename).trim());
      const payload = {
        timestamp: selected.timestamp,
        username: selected.username,
        reply: replyDraft ?? '',
        ...(hasFilename ? { filename: String(selected.filename).trim() } : {}),
      };

      const res = await fetch('/support-tickets/reply-close', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`Update failed (${res.status}) ${txt}`);
      }
      const json = await res.json();
      if (!json.ok || !json.updated) {
        throw new Error(`Ticket not updated (updated=${json.updated || 0}).`);
      }

      // Success: remove from list and go back
      setTickets((prev) =>
        prev.filter((t) => {
          const tsMatch = String(t.timestamp).trim() === String(selected.timestamp).trim();
          const userMatch =
            String(t.username).trim().toLowerCase() ===
            String(selected.username).trim().toLowerCase();

          if (!hasFilename) {
            // No filename provided: backend may have closed 1+ rows for this (ts, user)
            return !(tsMatch && userMatch);
          }
          const fileMatch = String(t.filename).trim() === String(selected.filename).trim();
          return !(tsMatch && userMatch && fileMatch);
        })
      );

      setSelected(null);
      setReplyDraft('');
    } catch (e) {
      console.error(e);
      setErr(e.message || 'Failed to update ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const styles = useMemo(() => createStyles(isMobile), [isMobile]);

  return (
    <div style={styles.backdropStyle}>
      <div style={styles.modalStyle}>
        <div style={styles.headerRowStyle}>
          <h3 style={{ margin: 0, fontSize: isMobile ? 16 : 18 }}>
            {selected ? 'Ticket Details' : 'Support Tickets'}
          </h3>

          <div style={{ display: 'flex', gap: 8 }}>
            {selected && (
              <button onClick={() => setSelected(null)} style={styles.buttonStyle('#f59e0b')}>
                Back
              </button>
            )}
            <button onClick={onClose} style={styles.buttonStyle('#b91c1c')}>
              Close
            </button>
          </div>
        </div>

        {!selected && (
          <>
            <div style={{ width: '100%', color: '#6b7280', marginTop: 6, fontSize: 13 }}>
              {loading ? 'Loading…' : err ? `Error: ${err}` : `Open requests: ${tickets.length}`}
            </div>

            {(!tickets || tickets.length === 0) && !loading && !err ? (
              <div style={styles.emptyStyle}>
                <div style={{ fontSize: 40, lineHeight: 1 }}>📝</div>
                <div style={{ fontWeight: 600 }}>No open support requests</div>
                <div style={{ fontSize: 14, color: '#6b7280' }}>
                  When users submit tickets, they’ll show up here.
                </div>
              </div>
            ) : null}

            {tickets && tickets.length > 0 ? (
              <div style={styles.tableWrapStyle}>
                {/* Header */}
                <div style={styles.tableHeaderStyle}>
                  <div style={{ ...styles.cellHeaderStyle, flex: 1.2 }}>
                    {isMobile ? 'User' : 'Username'}
                  </div>
                  <div style={{ ...styles.cellHeaderStyle, flex: isMobile ? 2.8 : 2.4 }}>
                    Title
                  </div>
                  {!isMobile && <div style={{ ...styles.cellHeaderStyle, flex: 1.4 }}>File</div>}
                </div>

                {/* Body */}
                <div style={styles.tableBodyStyle}>
                  {tickets.map((t, i) => {
                    const title = buildTitle(t);
                    const file = baseName(t.filename) || '—';
                    const secondary = isMobile ? file : '';

                    return (
                      <div
                        key={`${t.timestamp}-${t.username}-${t.filename || i}`}
                        style={styles.tableRowStyle}
                        onClick={() => onRowActivate(t)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          (e.key === 'Enter' || e.key === ' ') && onRowActivate(t)
                        }
                      >
                        <div style={{ ...styles.cellStyle, flex: 1.2, fontWeight: 600 }}>
                          {t.username || 'unknown'}
                        </div>

                        <div
                          style={{
                            ...styles.cellStyle,
                            flex: isMobile ? 2.8 : 2.4,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          <div style={{ fontWeight: 600 }}>{title}</div>
                          {secondary ? (
                            <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                              File: {secondary}
                            </div>
                          ) : null}
                        </div>

                        {!isMobile && (
                          <div style={{ ...styles.cellStyle, flex: 1.4 }}>{file}</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </>
        )}

        {selected && (
          <div style={styles.detailsWrapStyle}>
            <div style={styles.detailRowStyle}>
              <div style={styles.detailLabelStyle}>Title</div>
              <div style={styles.detailValueStyle}>{buildTitle(selected)}</div>
            </div>

            <div style={styles.detailRowStyle}>
              <div style={styles.detailLabelStyle}>Username</div>
              <div style={styles.detailValueStyle}>{selected.username || 'unknown'}</div>
            </div>

            <div style={styles.detailRowStyle}>
              <div style={styles.detailLabelStyle}>File</div>
              <div style={styles.detailValueStyle}>{baseName(selected.filename) || '—'}</div>
            </div>

            <div style={styles.detailRowStyle}>
              <div style={styles.detailLabelStyle}>Type</div>
              <div style={{ ...styles.detailValueStyle, textTransform: 'capitalize' }}>
                {selected.type || '—'}
              </div>
            </div>

            <div style={styles.detailRowStyle}>
              <div style={styles.detailLabelStyle}>Status</div>
              <div style={{ ...styles.detailValueStyle, textTransform: 'capitalize' }}>
                {selected.status || 'open'}
              </div>
            </div>

            <div style={styles.detailRowStyle}>
              <div style={styles.detailLabelStyle}>Timestamp</div>
              <div style={styles.detailValueStyle}>{selected.timestamp || '—'}</div>
            </div>

            <div style={styles.detailRowStyleColumn}>
              <div style={styles.detailLabelStyle}>Contents</div>
              <div style={styles.detailBlockStyle}>{(selected.contents || '').trim() || '—'}</div>
            </div>

            <div style={styles.detailRowStyleColumn}>
              <div style={styles.detailLabelStyle}>Reply</div>
              <div style={styles.detailBlockStyle}>{(selected.reply || '').trim() || '—'}</div>
            </div>

            {/* Reply editor */}
            <div style={styles.detailRowStyleColumn}>
              <div style={styles.detailLabelStyle}>Your reply</div>
              <textarea
                value={replyDraft}
                onChange={(e) => setReplyDraft(e.target.value)}
                placeholder="Type your reply..."
                style={styles.textAreaStyle}
                rows={isMobile ? 6 : 5}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={handleReplyAndClose}
                style={styles.buttonStyle('#16a34a')}
                disabled={submitting}
                title="Save reply and close this ticket"
              >
                {submitting ? 'Closing…' : 'Reply & Close Ticket'}
              </button>
            </div>
          </div>
        )}

        {err && selected && (
          <div style={{ color: '#b91c1c', marginTop: 6, fontSize: 13 }}>
            {String(err)}
          </div>
        )}
      </div>
    </div>
  );
}

function createStyles(isMobile) {
  const backdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: isMobile ? 'flex-end' : 'center',
    padding: isMobile ? 0 : 16,
    zIndex: 10000,
  };

  // On mobile: “bottom sheet” style, full width, safe padding, no minWidth
  const modalStyle = {
    backgroundColor: 'white',
    padding: isMobile ? 14 : 24,
    borderRadius: isMobile ? '14px 14px 0 0' : 12,
    width: isMobile ? '100%' : '70%',
    minWidth: isMobile ? undefined : 520,
    maxWidth: isMobile ? '100%' : 900,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    boxShadow: '0 0 15px rgba(0,0,0,0.3)',
    overflow: 'hidden',
    maxHeight: isMobile ? '92vh' : '80vh',
    boxSizing: 'border-box',
    // Respect iOS safe areas
    paddingBottom: isMobile ? 'calc(14px + env(safe-area-inset-bottom, 0px))' : 24,
  };

  const headerRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    alignItems: 'center',
    gap: 10,
  };

  const emptyStyle = {
    width: '100%',
    boxSizing: 'border-box',
    border: '1px dashed #d1d5db',
    borderRadius: 10,
    padding: isMobile ? 16 : 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8,
    background: '#fafafa',
  };

  const tableWrapStyle = {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    overflow: 'hidden',
    background: 'white',
    display: 'flex',
    flexDirection: 'column',
  };

  const tableHeaderStyle = {
    display: 'flex',
    background: '#f3f4f6',
    borderBottom: '1px solid #e5e7eb',
  };

  const tableBodyStyle = {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: isMobile ? '62vh' : '60vh',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  };

  const tableRowStyle = {
    display: 'flex',
    borderBottom: '1px solid #f3f4f6',
    alignItems: 'flex-start',
    cursor: 'pointer',
    transition: 'background 120ms ease',
  };

  const cellHeaderStyle = {
    padding: isMobile ? '10px 10px' : '10px 12px',
    fontSize: 13,
    fontWeight: 700,
    color: '#111827',
  };

  const cellStyle = {
    padding: isMobile ? '10px 10px' : '10px 12px',
    fontSize: isMobile ? 13 : 14,
    color: '#111827',
    boxSizing: 'border-box',
  };

  /* Details view styles */
  const detailsWrapStyle = {
    width: '100%',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: isMobile ? 12 : 16,
    background: 'white',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    maxHeight: isMobile ? '72vh' : '65vh',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  };

  // Mobile: stack label/value for readability
  const detailRowStyle = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '180px 1fr',
    alignItems: 'start',
    gap: isMobile ? 6 : 8,
  };

  const detailRowStyleColumn = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '180px 1fr',
    alignItems: 'start',
    gap: isMobile ? 6 : 8,
  };

  const detailLabelStyle = {
    fontSize: 13,
    color: '#6b7280',
    fontWeight: 600,
  };

  const detailValueStyle = {
    fontSize: 14,
    color: '#111827',
    wordBreak: 'break-word',
  };

  const detailBlockStyle = {
    fontSize: 14,
    color: '#111827',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    padding: '10px 12px',
    borderRadius: 8,
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
  };

  const textAreaStyle = {
    width: '100%',
    minHeight: isMobile ? 140 : 120,
    resize: 'vertical',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    fontSize: 14,
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  function buttonStyle(bg) {
    return {
      padding: isMobile ? '9px 12px' : '8px 14px',
      backgroundColor: bg,
      color: 'white',
      fontWeight: 'bold',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
      fontSize: 13,
      touchAction: 'manipulation',
    };
  }

  return {
    backdropStyle,
    modalStyle,
    headerRowStyle,
    emptyStyle,
    tableWrapStyle,
    tableHeaderStyle,
    tableBodyStyle,
    tableRowStyle,
    cellHeaderStyle,
    cellStyle,
    detailsWrapStyle,
    detailRowStyle,
    detailRowStyleColumn,
    detailLabelStyle,
    detailValueStyle,
    detailBlockStyle,
    textAreaStyle,
    buttonStyle,
  };
}
